import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  GridFilterItem,
  GridSelectedRowCount,
  GridToolbarContainer,
  gridColumnLookupSelector,
  gridFilterModelSelector,
  selectedGridRowsCountSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-pro';
import { Box, Button, IconButton, AppBar, Toolbar } from '@mui/material';
import { FaSearch } from 'react-icons/fa';
import { MdClose } from 'react-icons/md';
import {
  ControllerGridToolbarFilterButton,
  ControllerGridFilterInputValue,
  ControllerGridFilterInputAutocomplete,
  ControllerMenuItem,
  FormControl, // Causes console warnings when imported from MUI directly
  GridCombinedToolbarMenu,
  Select,
  SelectChangeEvent,
  InputAdornment,
} from '@bymason-private/legacy-pattern-components';
import { useNotifyDevices, NotifyType, NotificationType } from '@bymason/controller-api-hooks';
import { useAuth } from '@bymason-private/auth-provider';

import { useToast } from '../../../providers';
import { getBrowserStorageKey, persistGridDensity } from '../../../browser-storage';
import MoveDevicesModal from './move-devices-modal';

type DeviceSearchOperator = 'equals' | 'contains';
const deviceSearchOperatorKey = getBrowserStorageKey('device-search-operator');

export function DevicesToolbar() {
  const { authToken = '' } = useAuth();
  const apiRef = useGridApiContext();
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
  // TODO: add row count info back in when api is updated by SOF-1822
  // const totalTopLevelRowCount = useGridSelector(apiRef, gridTopLevelRowCountSelector);
  // const visibleTopLevelRowCount = useGridSelector(apiRef, gridVisibleTopLevelRowCountSelector);
  const selectedRowCount = useGridSelector(apiRef, selectedGridRowsCountSelector);
  const columnsLookup = useGridSelector(apiRef, gridColumnLookupSelector);

  const [setToastMessage, setToastSeverity] = useToast();
  const [notifyDevices, , notifyError, notifyBusy] = useNotifyDevices(authToken);
  const [pendingNotifyType, setPendingNotifyType] = useState<NotifyType | null>(null);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [matchType, setMatchType] = useState<DeviceSearchOperator>(
    localStorage?.getItem(deviceSearchOperatorKey) === 'equals' ? 'equals' : 'contains'
  );

  const filterMap = useMemo(
    () =>
      new Map(
        filterModel.items.reduce<Array<[string, GridFilterItem]>>((acc, item) => {
          return item.columnField ? [...acc, [item.columnField, item]] : acc;
        }, [])
      ),
    [filterModel]
  );

  const deviceGroupOptions = useMemo(() => {
    return (columnsLookup['deviceGroup'].valueOptions || []) as { label: string; value: string }[];
  }, [columnsLookup]);

  const applyFilter = useCallback(
    (item: GridFilterItem) => {
      apiRef.current.upsertFilterItem(item);
    },
    [apiRef]
  );

  const handleChangeOperator = useCallback(
    (event: SelectChangeEvent) => {
      const item = filterMap.get('all');
      const operator = event.target.value as 'equals' | 'contains';
      setMatchType(operator);
      localStorage?.setItem(deviceSearchOperatorKey, operator);
      if (item) {
        applyFilter({ ...item, operatorValue: operator });
      }
    },
    [applyFilter, filterMap]
  );

  const clearSelectedRows = useCallback(() => {
    apiRef.current.selectRows([], false, true);
  }, [apiRef]);

  const handleNotifyDevices = useCallback(
    (type: NotifyType) => () => {
      const selectedDeviceIds = Array.from(apiRef.current.getSelectedRows(), ([key]) => String(key));
      if (selectedDeviceIds.length) {
        notifyDevices({ type, devices: selectedDeviceIds });
        setPendingNotifyType(type);
      }
    },
    [apiRef, notifyDevices]
  );

  const handleMoveModalOpen = () => {
    setShowMoveModal(true);
  };
  const handleMoveModalClose = () => {
    setShowMoveModal(false);
  };

  useEffect(() => {
    const errorMessage =
      !notifyBusy && notifyError && pendingNotifyType ? `Failed to send ${pendingNotifyType} command` : '';

    if (errorMessage) {
      setToastSeverity('error');
      setToastMessage(errorMessage);
      setPendingNotifyType(null);
    }
  }, [notifyBusy, notifyError, pendingNotifyType, setToastMessage, setToastSeverity]);

  useEffect(() => {
    if (!notifyBusy && !notifyError && pendingNotifyType) {
      setToastSeverity('success');
      setToastMessage(`${pendingNotifyType} command sent`);
      setPendingNotifyType(null);
    }
  }, [notifyBusy, notifyError, pendingNotifyType, setToastMessage, setToastSeverity]);

  return (
    <GridToolbarContainer
      sx={{
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          '&>*': {
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          },
        }}
      >
        <div>
          <div>
            <GridCombinedToolbarMenu
              csvOptions={{ disableToolbarButton: true }}
              disableFilterMenuItem
              onDensityChange={persistGridDensity}
            />
          </div>
          <ControllerGridFilterInputAutocomplete
            item={filterMap.get('deviceGroup') ?? { columnField: 'deviceGroup' }}
            apiRef={apiRef}
            applyValue={applyFilter}
            label={undefined}
            fullWidth
            placeholder='All groups'
            sx={{ margin: 0 }}
          />
          <Box display='flex'>
            <ControllerGridFilterInputValue
              item={filterMap.get('all') ?? { columnField: 'all', operatorValue: matchType, value: '' }}
              apiRef={apiRef}
              applyValue={applyFilter}
              label={undefined}
              fullWidth
              placeholder={'Search IMEI/Serial/Name'}
              sx={{
                margin: 0,
                minWidth: '400px',
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <FaSearch size={16} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position='end'>
                    {filterMap.has('all') && (
                      <IconButton
                        onClick={() =>
                          apiRef.current.deleteFilterItem(filterMap.get('all') ?? { columnField: 'all' })
                        }
                      >
                        <MdClose />
                      </IconButton>
                    )}
                    <FormControl
                      color='secondary'
                      size='small'
                      margin='none'
                      sx={{
                        height: '32px',
                        minWidth: '100px',
                        opacity: 0.5,
                        '&:hover': {
                          opacity: 1,
                        },
                      }}
                    >
                      <Select
                        value={matchType}
                        onChange={handleChangeOperator}
                        sx={{
                          height: '32px',
                          fontSize: 14,
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                        }}
                      >
                        <ControllerMenuItem value='equals'>Equals</ControllerMenuItem>
                        <ControllerMenuItem value='contains'>Contains</ControllerMenuItem>
                      </Select>
                    </FormControl>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <div>
            <ControllerGridToolbarFilterButton />
          </div>
        </div>
        <div>
          {/* TODO: add row count info back in when api is updated by SOF-1822 */}
          {/* <GridRowCount rowCount={totalTopLevelRowCount} visibleRowCount={visibleTopLevelRowCount} /> */}
        </div>
      </Box>

      {selectedRowCount > 0 && (
        <AppBar
          position='absolute'
          // lower z-index so that it doesn't overlap FAB
          sx={(theme) => ({
            zIndex: theme.zIndex.fab,
            top: 'auto',
            bottom: 0,
            fontWeight: 'bold',
          })}
        >
          <Toolbar
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              '& > *': {
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              },
              '& button': {
                boxShadow: 'none',
              },
              '& button:hover': {
                boxShadow: 'none',
              },
            }}
          >
            <div>
              <Button
                variant='contained'
                onClick={handleNotifyDevices(NotificationType.Refresh)}
                disabled={pendingNotifyType === NotificationType.Refresh}
              >
                Ping
              </Button>
              <Button variant='contained' onClick={handleMoveModalOpen}>
                Move
              </Button>
              <Button
                variant='contained'
                onClick={handleNotifyDevices(NotificationType.Update)}
                disabled={pendingNotifyType === NotificationType.Update}
              >
                Check for Updates
              </Button>
            </div>
            <div>
              <GridSelectedRowCount selectedRowCount={selectedRowCount} />
              <Button variant='text' color='inherit' onClick={clearSelectedRows}>
                Clear
              </Button>
            </div>
          </Toolbar>
        </AppBar>
      )}
      <MoveDevicesModal
        open={showMoveModal}
        onClose={handleMoveModalClose}
        deviceGroupOptions={deviceGroupOptions}
      />
    </GridToolbarContainer>
  );
}
